
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Stack,
  Switch,
  IconButton,
  Popper,
  Link,
  ClickAwayListener
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import owlsIcon from "../assets/anon-icons/whimsi.png";

import ItemResult from "../components/search/ItemResult";
import TradeResult from "../components/search/TradeResult";
import { fetchWithAuth } from "../utils/api";
import RegularLinkButton from "../components/search/RegularLinkButton";
import { ReportersData } from "../data/ReportersData";

const SEARCH_REDIRECTS = new Map([
  ["Dyeworks Blue:Rainbow Petal Shower", "Dyeworks Blue: Rainbow Petal Shower"],
  ["Dyeworks White : Prism Light Filter", "Dyeworks White: Prism Light Filter"],
]);

const createReporterAliases = (reports) => {
  const reporterKeys = [
    ...new Set(
      reports
        .map((report) => report.reporterKey)
        .filter((key) => key != null)
    ),
  ];

  // Shuffle the available reporter aliases.
  const shuffledReporters = [...ReportersData];

  for (let i = shuffledReporters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    [shuffledReporters[i], shuffledReporters[j]] = [
      shuffledReporters[j],
      shuffledReporters[i],
    ];
  }

  return Object.fromEntries(
    reporterKeys.map((key, index) => [
      key,
      shuffledReporters[index],
    ])
  );
};

const SearchResults = () => {
  const { item } = useParams();
  const navigate = useNavigate();
  const [itemStats, setItemStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [events, setEvents] = useState([]);
  const reporterAliases = useMemo(
    () => createReporterAliases(reports),
    [reports]
  );

  const [showAnonymousReporters, setShowAnonymousReporters] = useState(true);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);

  const infoOpen = Boolean(infoAnchorEl);

  const handleInfoOpen = (event) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  useEffect(() => {
    const redirect = SEARCH_REDIRECTS.get(item);
    if (redirect) {
      navigate(`/search/${encodeURIComponent(redirect)}`, { replace: true });
    }
  }, [item, navigate]);

  useEffect(() => {
    if (SEARCH_REDIRECTS.has(item)) return;  // skip fetch if redirecting
    const fetchItem = async () => {
      setLoading(true);
      setError(null);
      setItemStats(null);
      setReports([]);
      setSuggestions([]);
      setEvents([]);

      try {
        const res = await fetchWithAuth(`/website-search/${encodeURIComponent(item)}`);
        setItemStats(res.itemStats ?? null);
        setEvents(Array.isArray(res.events) ? res.events : []);
        setReports(Array.isArray(res.reports) ? res.reports : []);
        setSuggestions(res.suggestions ?? []);
      } catch (err) {
        if (err.status === 429) {
          setError("rate-limited");
          return;
        }
        let parsedError;
        try {
          parsedError = JSON.parse(err.message);
        } catch {
          parsedError = {};
        }
        if (parsedError.error === "Item not found.") {
          setSuggestions(Array.isArray(parsedError.suggestions) ? parsedError.suggestions : []);
          setItemStats(null);
          setReports([]);
        }
        else {
          setError(`Failed to load item data for "${item}". Please try again or contact the team.`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (item) fetchItem();
  }, [item]);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && error !== "rate-limited" && <Alert severity="error">{error}</Alert>}

      {!loading && !error && itemStats && (
        <>
          <ItemResult
            itemStats={itemStats}
            mostRecentTradeDate={reports[0]?.tradeDate ?? null}
            events={events}
          />

          {reports.length > 0 && (
            <ClickAwayListener onClickAway={handleInfoClose}>
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton
                      size="small"
                      aria-label="About anonymous labels"
                      onClick={(event) => {
                        if (infoOpen) {
                          handleInfoClose();
                        } else {
                          handleInfoOpen(event);
                        }
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="body2" color="text.secondary">
                      Show anonymous labels
                    </Typography>

                    <Switch
                      size="small"
                      checked={showAnonymousReporters}
                      onChange={(event) =>
                        setShowAnonymousReporters(event.target.checked)
                      }
                      inputProps={{ "aria-label": "Show anonymous labels" }}
                    />
                  </Stack>
                </Box>

                <Popper
                  open={infoOpen}
                  anchorEl={infoAnchorEl}
                  placement="bottom-end"
                  sx={{
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      maxWidth: 300,
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      boxShadow: 3,
                      m: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Anonymous reporter labels indicate when multiple trade reports were
                      submitted by the same person.{" "}
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => {
                          handleInfoClose();
                          navigate(
                            "/articles/how-a-value-guide-works#anonymous-reporter-labels"
                          );
                        }}
                      >
                        Learn more
                      </Link>
                    </Typography>
                  </Box>
                </Popper>
              </Box>
            </ClickAwayListener>
          )}

          {reports.length === 0 ? (
            <Alert severity="info" sx={{ mt: 4 }}>
              No trade reports yet for this item.
            </Alert>
          ) : (
            <Box mt={2}>
              {reports.map((r, i) => (
                <TradeResult
                  key={r.id ?? i}
                  trade={r}
                  highlightTerm={item}
                  showAnonymousReporter={showAnonymousReporters}
                  anonymousReporter={
                    r.isLegacyReporter
                      ? {
                        name: "The Owls Team",
                        image: owlsIcon,
                      }
                      : reporterAliases[r.reporterKey]
                  }
                />
              ))}
              <Typography textAlign="center">
                - End of the latest {reports.length} trade reports -
              </Typography>
            </Box>
          )}
        </>
      )}

      {!loading && !error && !itemStats && (
        <Box textAlign="center" mt={4}>
          <Typography variant="h5" gutterBottom>
            "{item}" not found.
          </Typography>
          <Typography variant="body1" mb={2}>
            We couldn't find a match for that item.
          </Typography>

          {suggestions.length > 0 && (
            <Box textAlign="center">
              <Typography variant="body2" mb={1}>
                Did you mean:
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
              >
                {suggestions.map((s, i) => (
                  <RegularLinkButton
                    variant="outlined"
                    key={i}
                    text={s}
                    linkURL={`/search/${encodeURIComponent(s)}`}
                  />
                ))}
              </Stack>
            </Box>
          )}

        </Box>
      )}
    </Container>
  );
};

export default SearchResults;
